import * as React from 'react'
import { useEffect, useRef, useState } from 'react'

import { cn } from '@/lib'

import { cva, VariantProps } from 'class-variance-authority'

const inputVariants = cva(
  'px-5 py-3 w-full bg-white-4 rounded-sm ring-[1px] md:ring-[0.5px] [@media(max-resolution:1.9dppx)]:ring-[1px] focus:outline-none ring-white-10 text-size-14 placeholder:text-white-48 disabled:ring-transparent disabled:cursor-not-allowed disabled:text-white-48 disabled:hover:ring-transparent disabled:focus:ring-transparent group-hover:ring-[1px] group-hover:ring-main-100 disabled:group-hover:ring-transparent disabled:group-hover:shadow-none',
  {
    variants: {
      variant: {
        default: '',
        destructive: 'ring-red-100 group-hover:ring-red-100 text-red-100 ring-[1px]',
      },
      size: {
        default: 'h-12',
        sm: 'h-10',
        lg: 'h-14',
      },
    },

    compoundVariants: [],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'prefix' | 'size'>,
    VariantProps<typeof inputVariants> {
  prefix?: React.ReactNode
  suffix?: React.ReactNode
  wrapperClassName?: string
  disabled?: boolean
}
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, prefix, suffix, variant, size, disabled, ...props }, ref) => {
    return (
      <div
        className={cn('relative w-full group rounded-sm', {
          'dark:hover:shadow-input': !disabled,
        })}
      >
        {prefix && <div className='absolute left-3 z-[1] top-0 bottom-0 flex items-center'>{prefix}</div>}
        <input
          type={type}
          className={cn(
            inputVariants({ variant, size }),
            {
              'pl-14': prefix,
              'pr-10': suffix,
            },
            className
          )}
          ref={ref}
          disabled={disabled}
          {...props}
        />
        {suffix && (
          <div
            className={cn('absolute right-3 top-1/2 -translate-y-1/2 flex items-center', {
              'pointer-events-none text-white-48': disabled,
            })}
          >
            {suffix}
          </div>
        )}
      </div>
    )
  }
)

const AutoResizingInput = ({
  placeholder = '',
  value = '',
  className,
  ...props
}: Omit<InputProps, 'prefix' | 'suffix' | 'size' | 'variant'>) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const sizerRef = useRef<HTMLSpanElement>(null)
  const [inputWidth, setInputWidth] = useState('auto')

  const resizeInput = () => {
    if (sizerRef.current) {
      setInputWidth(`${sizerRef.current.offsetWidth + 4}px`)
    }
  }

  React.useLayoutEffect(() => {
    resizeInput() // Ensure correct width on mount
  }, [])

  useEffect(() => {
    resizeInput()
  }, [value, placeholder]) // Resize when value or placeholder changes

  return (
    <div style={{ display: 'inline-block', position: 'relative' }}>
      <input
        ref={inputRef}
        placeholder={placeholder}
        value={value}
        className={className}
        style={{
          width: inputWidth,
          maxWidth: '100%',
        }}
        {...props}
      />
      <span
        className={className}
        ref={sizerRef}
        style={{
          position: 'absolute',
          whiteSpace: 'pre',
          visibility: 'hidden',
          width: 'fit-content',
        }}
      >
        {value || placeholder || ' '}
      </span>
    </div>
  )
}

export { AutoResizingInput, Input }
